"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef, memo, useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { proxyImageUrl } from "@/lib/image-proxy";
import { UserAvatar } from "@/components/ui/user-avatar";

/**
 * MorphingUpgradeText - Personalized morphing messages for the upgrade header
 *
 * Features:
 * - 3 messages cycling with smooth opacity/scale animations
 * - Avatar always visible for visual consistency (no layout gaps)
 * - Fixed height container to prevent layout shifts
 * - Dark skeleton loaders (bg-white/10)
 * - IntersectionObserver for performance
 * - Respects prefers-reduced-motion
 */

interface MorphingUpgradeTextProps {
  /** User's avatar URL */
  avatarUrl?: string | null;
  /** User's username */
  username?: string | null;
  /** Whether data is still loading */
  isLoading?: boolean;
  /** Interval between message changes in ms */
  morphInterval?: number;
}

// Animation config - matches MorphingLogo pattern
const morphTransition = {
  duration: 0.5,
  ease: [0.4, 0, 0.2, 1]
} as const;

// Dark skeleton for loading state
function DarkSkeleton(t0) {
  const $ = _c(3);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      background: "linear-gradient(90deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)",
      backgroundSize: "200% 100%",
      animation: "skeleton-shimmer 1.5s ease-in-out infinite"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = <div className={className} style={t1} />;
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

// Avatar component - uses UserAvatar for consistent fallback handling and caching
function Avatar(t0) {
  const $ = _c(6);
  const {
    src,
    username,
    isLoading
  } = t0;
  if (isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <DarkSkeleton className="h-5 w-5 shrink-0 rounded-full" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== src) {
    t1 = src ? proxyImageUrl(src) : null;
    $[1] = src;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const proxiedUrl = t1;
  const t2 = username || "user";
  let t3;
  if ($[3] !== proxiedUrl || $[4] !== t2) {
    t3 = <UserAvatar src={proxiedUrl} username={t2} size={20} rounded="full" ring={false} className="ring-1 ring-white/20" />;
    $[3] = proxiedUrl;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

// Message content renderer - keeps avatar visible, morphs only the text
function MessageContent(t0) {
  const $ = _c(9);
  const {
    index,
    username
  } = t0;
  switch (index) {
    case 0:
      {
        const t1 = username || "you";
        let t2;
        if ($[0] !== t1) {
          t2 = <span className="font-semibold text-white">@{t1}</span>;
          $[0] = t1;
          $[1] = t2;
        } else {
          t2 = $[1];
        }
        let t3;
        let t4;
        let t5;
        if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <span className="text-gray-200"> could gain </span>;
          t4 = <span className="font-semibold text-white">3,849</span>;
          t5 = <span className="text-gray-200"> followers/mo</span>;
          $[2] = t3;
          $[3] = t4;
          $[4] = t5;
        } else {
          t3 = $[2];
          t4 = $[3];
          t5 = $[4];
        }
        let t6;
        if ($[5] !== t2) {
          t6 = <>{t2}{t3}{t4}{t5}</>;
          $[5] = t2;
          $[6] = t6;
        } else {
          t6 = $[6];
        }
        return t6;
      }
    case 1:
      {
        let t1;
        if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <><span className="text-gray-200">Upgrade and grow </span><span className="font-semibold text-white">2.75x faster</span></>;
          $[7] = t1;
        } else {
          t1 = $[7];
        }
        return t1;
      }
    case 2:
      {
        let t1;
        if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <><span className="text-gray-200">AI targeting is </span><span className="font-semibold text-white">ready for you</span></>;
          $[8] = t1;
        } else {
          t1 = $[8];
        }
        return t1;
      }
    default:
      {
        return null;
      }
  }
}
function MorphingUpgradeTextInner(t0) {
  const $ = _c(28);
  const {
    avatarUrl,
    username,
    isLoading: t1,
    morphInterval: t2
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const morphInterval = t2 === undefined ? 5000 : t2;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const containerRef = useRef(null);
  const shouldReduceMotion = useReducedMotion();
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (!containerRef.current) {
        return;
      }
      const observer = new IntersectionObserver(t5 => {
        const [entry] = t5;
        return setIsVisible(entry.isIntersecting);
      }, {
        threshold: 0.1
      });
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    };
    t4 = [];
    $[0] = t3;
    $[1] = t4;
  } else {
    t3 = $[0];
    t4 = $[1];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[2] !== isLoading || $[3] !== isVisible || $[4] !== morphInterval || $[5] !== shouldReduceMotion) {
    t5 = () => {
      if (shouldReduceMotion || !isVisible || isLoading) {
        return;
      }
      const intervalId = setInterval(() => {
        setCurrentIndex(_temp);
      }, morphInterval);
      return () => clearInterval(intervalId);
    };
    t6 = [shouldReduceMotion, isVisible, isLoading, morphInterval];
    $[2] = isLoading;
    $[3] = isVisible;
    $[4] = morphInterval;
    $[5] = shouldReduceMotion;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  if (isLoading) {
    let t7;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div ref={containerRef} className="flex h-5 items-center gap-2"><DarkSkeleton className="h-5 w-5 shrink-0 rounded-full" /><DarkSkeleton className="h-4 w-48 rounded sm:w-64" /></div>;
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    return t7;
  }
  if (shouldReduceMotion) {
    let t7;
    if ($[9] !== avatarUrl || $[10] !== username) {
      t7 = <Avatar src={avatarUrl} username={username} />;
      $[9] = avatarUrl;
      $[10] = username;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let t8;
    if ($[12] !== username) {
      t8 = <span className="whitespace-nowrap text-sm"><MessageContent index={0} username={username} /></span>;
      $[12] = username;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    let t9;
    if ($[14] !== t7 || $[15] !== t8) {
      t9 = <div ref={containerRef} className="flex h-5 items-center gap-2">{t7}{t8}</div>;
      $[14] = t7;
      $[15] = t8;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    return t9;
  }
  let t7;
  if ($[17] !== avatarUrl || $[18] !== username) {
    t7 = <Avatar src={avatarUrl} username={username} />;
    $[17] = avatarUrl;
    $[18] = username;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      contain: "layout"
    };
    t9 = [0, 1, 2];
    $[20] = t8;
    $[21] = t9;
  } else {
    t8 = $[20];
    t9 = $[21];
  }
  let t10;
  if ($[22] !== currentIndex || $[23] !== username) {
    t10 = <div className="relative flex h-5 items-center" style={t8}>{t9.map(index => {
        const isActive = index === currentIndex;
        return <motion.span key={index} initial={false} animate={{
          opacity: isActive ? 1 : 0,
          scale: isActive ? 1 : 0.95
        }} transition={morphTransition} className="whitespace-nowrap text-sm" style={{
          position: index === 0 ? "relative" : "absolute",
          left: 0,
          pointerEvents: isActive ? "auto" : "none"
        }}><MessageContent index={index} username={username} /></motion.span>;
      })}</div>;
    $[22] = currentIndex;
    $[23] = username;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t7) {
    t11 = <div ref={containerRef} className="flex h-5 items-center gap-2">{t7}{t10}</div>;
    $[25] = t10;
    $[26] = t7;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
}

// Memoize to prevent unnecessary re-renders
function _temp(prev) {
  return (prev + 1) % 3;
}
const MorphingUpgradeText = memo(MorphingUpgradeTextInner, (prev, next) => {
  return prev.avatarUrl === next.avatarUrl && prev.username === next.username && prev.isLoading === next.isLoading && prev.morphInterval === next.morphInterval;
});
MorphingUpgradeText.displayName = "MorphingUpgradeText";
export default MorphingUpgradeText;